import { router } from '%app/js/common/services/router';
import readServerError from '%app/js/common/services/readServerError';
import { carriesReadableErrorBody } from '%app/js/common/services/readClientErrorEnvelope';

const RESOURCE_ERROR_KINDS = Object.freeze({
    NOT_FOUND: 'not_found',
    NETWORK: 'network',
    INVALID_STATE: 'invalid_state',
    INVALID_STEP: 'invalid_step',
    INVALID_PARAMETERS: 'invalid_parameters',
    PROJECT_UNAPPROVED: 'project_unapproved',
    PAYMENT_METHODS_UNAVAILABLE: 'payment_methods_unavailable',
    RATE_LIMITED: 'rate_limited',
});

const TOO_MANY_REQUESTS_STATUS = 429;
const RETRY_AFTER_HEADER = 'Retry-After';
const MILLISECONDS_PER_SECOND = 1000;

/* eslint-disable babel/camelcase */
const SERVER_ERROR_CODE_TO_KIND = Object.freeze({
    invalid_state: RESOURCE_ERROR_KINDS.INVALID_STATE,
    invalid_step: RESOURCE_ERROR_KINDS.INVALID_STEP,
    invalid_parameters: RESOURCE_ERROR_KINDS.INVALID_PARAMETERS,
    not_found: RESOURCE_ERROR_KINDS.NOT_FOUND,
    project_unapproved: RESOURCE_ERROR_KINDS.PROJECT_UNAPPROVED,
    payment_methods_unavailable: RESOURCE_ERROR_KINDS.PAYMENT_METHODS_UNAVAILABLE,
});
/* eslint-enable babel/camelcase */

class JsonResourceFetchError extends Error {
    constructor(
        kind,
        message,
        serverErrorCode = null,
        serverErrorDescription = null,
        retryAfterMs = null,
    ) {
        super(message || `Failed to fetch resource (${kind})`);
        this.name = 'JsonResourceFetchError';
        this.kind = kind;
        this.serverErrorCode = serverErrorCode;
        this.serverErrorDescription = serverErrorDescription;
        this.retryAfterMs = retryAfterMs;
        Object.setPrototypeOf(this, JsonResourceFetchError.prototype);
    }
}

function readRetryAfterMs(headers) {
    if (!headers || typeof headers.get !== 'function') {
        return null;
    }

    const value = headers.get(RETRY_AFTER_HEADER);
    const seconds = Number(value);
    const delayMs = Number.isFinite(seconds)
        ? seconds * MILLISECONDS_PER_SECOND
        : Date.parse(value) - Date.now();

    return Number.isFinite(delayMs) && delayMs > 0 ? delayMs : null;
}

async function readJsonBody(response) {
    try {
        return await response.json();
    } catch (parseError) {
        return null;
    }
}

function readErrorKind(error) {
    return error instanceof JsonResourceFetchError ? error.kind : RESOURCE_ERROR_KINDS.NETWORK;
}

function readServerErrorKind(serverErrorCode) {
    return typeof serverErrorCode === 'string'
        && Object.prototype.hasOwnProperty.call(SERVER_ERROR_CODE_TO_KIND, serverErrorCode)
        ? SERVER_ERROR_CODE_TO_KIND[serverErrorCode]
        : undefined;
}

function readServerFetchErrorKind(status, serverErrorCode) {
    return status === TOO_MANY_REQUESTS_STATUS
        ? RESOURCE_ERROR_KINDS.RATE_LIMITED
        : readServerErrorKind(serverErrorCode);
}

function createServerFetchError(status, body, retryAfterMs = null) {
    const serverError = readServerError(body);
    const kind = readServerFetchErrorKind(status, serverError.code);

    if (!carriesReadableErrorBody(status) && kind === undefined) {
        return new JsonResourceFetchError(RESOURCE_ERROR_KINDS.NETWORK, `HTTP ${status}`, null, null, retryAfterMs);
    }

    return new JsonResourceFetchError(
        kind || RESOURCE_ERROR_KINDS.NETWORK,
        `HTTP ${status}`,
        serverError.code,
        serverError.description,
        retryAfterMs,
    );
}

async function fetchJsonResponse(routeName, params, { signal, method = 'GET', body } = {}) {
    const requestInit = {
        method: method || 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal,
    };

    if (body !== undefined) {
        requestInit.headers['Content-Type'] = 'application/json';
        requestInit.body = JSON.stringify(body);
    }

    const response = await fetch(router.generate(routeName, params), requestInit);

    const retryAfterMs = readRetryAfterMs(response.headers);

    if (response.status === 204) {
        return {
            status: 204, ok: response.ok, body: null, retryAfterMs,
        };
    }

    return {
        status: response.status,
        ok: response.ok,
        body: await readJsonBody(response),
        retryAfterMs,
    };
}

async function fetchJsonResource(routeName, params, { signal, method = 'GET', body } = {}) {
    let result;

    try {
        result = await fetchJsonResponse(routeName, params, { signal, method, body });
    } catch (cause) {
        if (cause.name === 'AbortError') {
            throw cause;
        }

        throw new JsonResourceFetchError(RESOURCE_ERROR_KINDS.NETWORK, cause.message);
    }

    if (result.status === 404) {
        throw new JsonResourceFetchError(RESOURCE_ERROR_KINDS.NOT_FOUND);
    }

    if (result.status === 204) {
        return null;
    }

    if (!result.ok) {
        throw createServerFetchError(result.status, result.body, result.retryAfterMs);
    }

    if (result.body === null) {
        throw new JsonResourceFetchError(RESOURCE_ERROR_KINDS.NETWORK, 'Invalid JSON body');
    }

    return result.body;
}

export {
    createServerFetchError,
    fetchJsonResource,
    fetchJsonResponse,
    readErrorKind,
    JsonResourceFetchError,
    RESOURCE_ERROR_KINDS,
};
